import { Badge, Button, cn } from '@fc/ui'
import { ArrowRight, Building2, Check, Coins, Info, KeyRound, Sparkles, Tag } from 'lucide-react'
import { useState } from 'react'

const BETA_URL = 'https://www.fosschurch.com/'

type Billing = 'monthly' | 'annual'

type Tier = {
  name: string
  /** Monthly price in USD, or null for custom/contact pricing. */
  priceMonthly: number | null
  /** Annual total in USD, or null when annual billing isn't offered. */
  priceAnnual: number | null
  tagline: string
  highlight?: boolean
  features: string[]
}

const tiers: Tier[] = [
  {
    name: 'Free',
    priceMonthly: 0,
    priceAnnual: null,
    tagline: 'Everything a small church needs to get going.',
    features: [
      'Unlimited people, forever',
      '1,000 member logins',
      '10 admin users · 5 groups',
      '10 GB content storage',
      'Text chat',
      '25 AI credits / month',
      'Giving (donor covers the fee)',
    ],
  },
  {
    name: 'Basic',
    priceMonthly: 40,
    priceAnnual: 440,
    tagline: 'For growing churches ready for the full toolkit.',
    features: [
      'Everything in Free, plus:',
      'Unlimited member logins',
      '10 admin users · 10 groups',
      '50 GB content · 500 GB collaboration',
      'Collab: chat, email, calendar & docs',
      '250 AI credits / month',
      'Pass-through giving · 3-day support',
    ],
  },
  {
    name: 'Plus',
    priceMonthly: 80,
    priceAnnual: 880,
    tagline: 'More room, hosted email, and multi-site.',
    highlight: true,
    features: [
      'Everything in Basic, plus:',
      '15 admin users · 20 groups',
      '100 GB content · 1 TB collaboration',
      'Hosted email + custom domain',
      'Multi-site campuses',
      '1,200 AI credits / month',
      '2-day support SLA',
    ],
  },
  {
    name: 'Pro',
    priceMonthly: 160,
    priceAnnual: 1760,
    tagline: 'For large, multi-campus ministries.',
    features: [
      'Everything in Plus, plus:',
      '20 admin users · 40 groups',
      '200 GB content · 2 TB collaboration',
      '2,500 AI credits / month',
      'Priority 24-hour support SLA',
    ],
  },
]

function priceLabel(tier: Tier, billing: Billing): { big: string; sub: string } {
  if (tier.priceMonthly === null) return { big: 'Custom', sub: 'Tailored to your ministry' }
  if (tier.priceMonthly === 0) return { big: '$0', sub: 'Free, forever' }
  if (billing === 'annual' && tier.priceAnnual) {
    const perMonth = Math.round(tier.priceAnnual / 12)
    return { big: `$${perMonth}`, sub: `$${tier.priceAnnual} billed yearly` }
  }
  return { big: `$${tier.priceMonthly}`, sub: 'billed monthly' }
}

export function Pricing() {
  const [billing, setBilling] = useState<Billing>('monthly')

  return (
    <section id="pricing" className="mx-auto max-w-[1140px] px-5 py-20">
      {/* Header */}
      <div className="mx-auto max-w-2xl text-center">
        <Badge variant="gold" className="mb-5">
          <Tag className="size-3" />
          Simple, honest pricing
        </Badge>
        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Plans that grow <span className="text-gradient">with your church</span>.
        </h2>
        <p className="mt-4 text-muted">
          People are always unlimited and free. Pay only for the admin team, storage, and AI you
          actually use — and bring your own API key any time to skip AI credits entirely.
        </p>
      </div>

      {/* Beta disclaimer */}
      <div className="mx-auto mt-8 flex max-w-2xl items-start gap-3 rounded-xl border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-muted">
        <Info className="mt-0.5 size-4 shrink-0 text-gold" />
        <p>
          <span className="font-medium text-fg">Potential pricing for our upcoming beta.</span>{' '}
          Actual pricing may change once the product launches. Open signups aren’t available yet —{' '}
          <a
            href={BETA_URL}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-secondary underline-offset-4 hover:underline"
          >
            submit a service request
          </a>{' '}
          to join the beta group.
        </p>
      </div>

      {/* Billing toggle */}
      <div className="mt-9 flex items-center justify-center gap-3">
        <div
          role="group"
          aria-label="Billing period"
          className="inline-flex items-center rounded-full border border-border bg-card p-1"
        >
          {(['monthly', 'annual'] as const).map((period) => (
            <button
              key={period}
              type="button"
              aria-pressed={billing === period}
              onClick={() => setBilling(period)}
              className={cn(
                'rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-colors',
                billing === period ? 'bg-brand-grad text-primary-fg' : 'text-muted hover:text-fg',
              )}
            >
              {period}
            </button>
          ))}
        </div>
        <Badge variant="success">Save ~8% yearly</Badge>
      </div>

      {/* Tier cards */}
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {tiers.map((tier) => {
          const { big, sub } = priceLabel(tier, billing)
          return (
            <div
              key={tier.name}
              className={cn(
                'relative flex flex-col rounded-2xl border p-6 transition-colors',
                tier.highlight
                  ? 'border-primary/60 bg-card-hover shadow-[0_0_40px_-20px_var(--primary)]'
                  : 'border-border bg-card hover:bg-card-hover',
              )}
            >
              {tier.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-grad px-3 py-0.5 text-xs font-semibold text-primary-fg">
                  Most popular
                </span>
              )}
              <h3 className="font-display text-lg font-semibold">{tier.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-4xl font-extrabold tracking-tight">{big}</span>
                {tier.priceMonthly !== null && tier.priceMonthly > 0 && (
                  <span className="text-sm text-dim">/mo</span>
                )}
              </div>
              <p className="mt-1 text-xs text-dim">{sub}</p>
              <p className="mt-4 min-h-[2.5rem] text-sm text-muted">{tier.tagline}</p>
              <Button
                asChild
                variant={tier.highlight ? 'primary' : 'secondary'}
                className="mt-5 w-full"
              >
                <a href={BETA_URL} target="_blank" rel="noreferrer">
                  Request beta access
                  <ArrowRight />
                </a>
              </Button>
              <ul className="mt-6 space-y-2.5">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                    <span
                      className={cn(f.endsWith('plus:') ? 'font-medium text-fg' : 'text-muted')}
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>

      {/* Enterprise banner */}
      <div className="mt-6 grid items-center gap-6 rounded-2xl border border-border bg-bg-alt p-8 lg:grid-cols-[1fr_auto]">
        <div>
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-10 place-items-center rounded-lg bg-brand-grad-soft text-secondary">
              <Building2 className="size-5" />
            </span>
            <h3 className="font-display text-xl font-bold tracking-tight">Enterprise</h3>
            <Badge variant="primary">Custom</Badge>
          </div>
          <p className="max-w-2xl text-sm text-muted">
            Everything in Pro, plus unlimited admins, custom limits, and optional voice &amp; video
            chat — one all-in-one bundle billed by FOSS Church instead of juggling vendors. Custom
            domain included, phone support, and a negotiated rate that fits your ministry.
          </p>
        </div>
        <Button asChild size="lg" variant="outline" className="lg:w-auto">
          <a href={BETA_URL} target="_blank" rel="noreferrer">
            Talk to us
            <ArrowRight />
          </a>
        </Button>
      </div>

      {/* AI credits explainer */}
      <div className="mt-10 grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-3 flex items-center gap-2.5">
            <Coins className="size-5 text-gold" />
            <h3 className="font-display text-base font-semibold">How AI credits work</h3>
          </div>
          <ul className="space-y-2.5 text-sm text-muted">
            <li className="flex items-start gap-2.5">
              <Check className="mt-0.5 size-4 shrink-0 text-primary" />
              You only ever see <span className="font-medium text-fg">credits</span> — no token
              math.
            </li>
            <li className="flex items-start gap-2.5">
              <Check className="mt-0.5 size-4 shrink-0 text-primary" />
              Choose your model org-wide or let each admin pick. Smarter models simply use more
              credits per request.
            </li>
            <li className="flex items-start gap-2.5">
              <KeyRound className="mt-0.5 size-4 shrink-0 text-secondary" />
              <span>
                <span className="font-medium text-fg">Bring your own key</span> on any tier to use
                AI without spending credits at all.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <Sparkles className="mt-0.5 size-4 shrink-0 text-gold" />
              Need more? Credit packs: 250 / $10 · 1,000 / $35 · 3,000 / $90 (available on every
              tier, Free included).
            </li>
          </ul>
        </div>

        {/* Add-ons & fine print */}
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-3 flex items-center gap-2.5">
            <Tag className="size-5 text-secondary" />
            <h3 className="font-display text-base font-semibold">Add-ons &amp; details</h3>
          </div>
          <ul className="space-y-2.5 text-sm text-muted">
            <li>
              <span className="font-medium text-fg">Additional storage</span> (collab or content):
              $0.10 · $0.08 · $0.06 · $0.04 per GB/mo (Free → Pro), Enterprise custom.
            </li>
            <li>
              <span className="font-medium text-fg">Multi-site</span> on paid plans: base price plus
              50% per additional campus.
            </li>
            <li>
              <span className="font-medium text-fg">Custom domain &amp; hosted email</span> on Plus
              and Pro: domain cost passed through plus a hosted-email fee.
            </li>
            <li>
              <span className="font-medium text-fg">Giving</span>: 2.9% + $0.30 card · 0.8% ACH.
              “Cover the fee” passes processing fees to the donor; paid plans can toggle it off and
              cover fees themselves.
            </li>
          </ul>
        </div>
      </div>
    </section>
  )
}
